import { useEffect, useState } from 'react';
import Typography from 'components/atoms/Typography';
import WysiwygEditor from 'components/atoms/WysiwygEditor';
import Input from 'components/molecules/Input';
import { ToggleControlled } from 'components/molecules/Toggle';
import CloudinaryImageUploader from 'components/organisms/CloudinaryImageUpload';
import { Templates, TemplateType } from 'utils/gloo-types';
import SelectKeywords from 'components/molecules/SelectKeywords';
import SeriesTemplateConfig from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm/SeriesTemplateConfig';
import KeywordTemplateConfig from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm/KeywordTemplateConfig';
import BroadcastTemplateConfig from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm/BroadcastTemplateConfig';
import LinkTemplateConfig from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm/LinkTemplateConfig';
import Card from 'components/atoms/Card';
import DownloadTemplateConfig from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm/DownloadTemplateConfig';
import SurveyTemplateConfig from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm/SurveyTemplateConfig';
import ScholarshipTemplateConfig from 'pages/_AdminAuthoringPages/_components/AuthoringTemplateForm/ScholarshipTemplateConfig';
import Select from 'components/molecules/Select';
import { orgTypeFilteringOptions } from 'utils/selectOptions';
import { SelectOption } from 'utils/frontendTypes';
import VideoTemplateConfig from './VideoTemplateConfig';

interface TemplateFormProps {
  formId: string;
  template?: Partial<Templates>;
  handleChange: (prevState: any) => void;
  handleSubmit: (data: any) => void;
}

function TemplateForm({ formId, template, handleChange, handleSubmit }: TemplateFormProps) {
  // For backward compatibility, if tags were set previously but topics are empty,
  // populate the topics with the tags so that the topics will exist in the row upon save
  if (!template?.metadata?.topics && template?.tags) {
    template.metadata = {
      ...template?.metadata,
      topics: [...template?.tags],
    };
  }

  const [updatedTemplate, setUpdatedTemplate] = useState<Partial<Templates>>(template || null);

  useEffect(() => {
    if (template?.archivedAt) {
      setUpdatedTemplate({ ...updatedTemplate, visibleInDiscover: false });
    }
  }, [template?.archivedAt]);

  const renderTemplateConfigPartial = (t: Partial<Templates>) => {
    switch (t.type) {
      case TemplateType.BROADCAST:
        return (
          <BroadcastTemplateConfig
            template={updatedTemplate}
            handleChange={setUpdatedTemplate}
            handleForm={handleChange}
          />
        );
      case TemplateType.DOWNLOAD:
        return (
          <DownloadTemplateConfig
            template={updatedTemplate}
            handleChange={setUpdatedTemplate}
            handleForm={handleChange}
          />
        );
      case TemplateType.KEYWORD:
        return (
          <KeywordTemplateConfig
            template={updatedTemplate}
            handleChange={setUpdatedTemplate}
            handleForm={handleChange}
          />
        );
      case TemplateType.LINK:
        return (
          <LinkTemplateConfig template={updatedTemplate} handleChange={setUpdatedTemplate} handleForm={handleChange} />
        );
      case TemplateType.SCHOLARSHIP:
        return (
          <ScholarshipTemplateConfig
            template={updatedTemplate}
            handleChange={setUpdatedTemplate}
            handleForm={handleChange}
          />
        );
      case TemplateType.VIDEO:
        return (
          <VideoTemplateConfig template={updatedTemplate} handleChange={setUpdatedTemplate} handleForm={handleChange} />
        );
      case TemplateType.SERIES:
        return (
          <SeriesTemplateConfig
            template={updatedTemplate}
            handleChange={setUpdatedTemplate}
            handleForm={handleChange}
          />
        );
      case TemplateType.SURVEY:
        return (
          <SurveyTemplateConfig
            template={updatedTemplate}
            handleChange={setUpdatedTemplate}
            handleForm={handleChange}
          />
        );
      default:
        return <div>A type is required</div>;
    }
  };

  return (
    <form
      id={formId}
      onSubmit={(e) => {
        e.stopPropagation();
        e.preventDefault();
        handleSubmit(updatedTemplate);
      }}
    >
      <Input
        value={template?.title || ''}
        label="Title"
        className="mb-4"
        onChangeText={(t) => {
          handleChange((prevState) => {
            return { ...prevState, title: t || undefined };
          });
          setUpdatedTemplate((prevState) => {
            return { ...prevState, title: t || undefined };
          });
        }}
        required
      />
      <Input
        value={template?.shortDescription || ''}
        label="Short Description"
        isMultiLine
        rows={4}
        className="mb-4"
        onChangeText={(t) => {
          handleChange((prevState) => {
            return { ...prevState, shortDescription: t || undefined };
          });
          setUpdatedTemplate((prevState) => {
            return { ...prevState, shortDescription: t || undefined };
          });
        }}
        required
      />
      <div className="mb-4">
        <Typography className="text-sm text-glooCharcoal font-semibold pb-2" variant="base">
          Display In Markeplace
        </Typography>
        <ToggleControlled
          isChecked={!!template?.visibleInDiscover}
          onChange={(b) => {
            handleChange((prevState) => {
              return { ...prevState, visibleInDiscover: b };
            });
            setUpdatedTemplate((prevState) => {
              return { ...prevState, visibleInDiscover: b };
            });
          }}
        />
      </div>
      <div className="mb-4">
        <Typography className="text-sm text-glooCharcoal font-semibold pb-2" variant="base">
          Org Type Filtering
        </Typography>
        <Select
          dataDurableId="orgTypeFilteringSelect"
          options={orgTypeFilteringOptions}
          onChange={(option: SelectOption) => {
            handleChange((prevState) => {
              return { ...prevState, orgTypeFiltering: option.value };
            });
            setUpdatedTemplate((prevState) => {
              return { ...prevState, orgTypeFiltering: option.value };
            });
          }}
          value={orgTypeFilteringOptions.find((o) => o.value === template?.orgTypeFiltering)}
        />
      </div>

      <SelectKeywords
        dataDurableId="templateFormSelectId"
        className="border mb-4 text-sm"
        label={
          <Typography className="text-sm text-glooCharcoal font-semibold" variant="base">
            Topics
          </Typography>
        }
        placeholder="Choose Topics"
        noOptionsMessage={() => 'Type a Topic and press Enter'}
        startingKeywords={template?.metadata?.topics || []}
        onChange={(t) => {
          handleChange((prevState) => {
            return {
              ...prevState,
              tags: [...t],
              metadata: {
                ...prevState.metadata,
                topics: [...t],
              },
            };
          });

          setUpdatedTemplate((prevState) => {
            return {
              ...prevState,
              tags: [...t],
              metadata: {
                ...prevState.metadata,
                topics: [...t],
              },
            };
          });
        }}
        onCreate={(t) => {
          handleChange((prevState) => {
            return {
              ...prevState,
              tags: [...prevState?.tags, t],
              metadata: {
                ...prevState.metadata,
                topics: prevState?.metadata?.topics ? [...prevState.metadata.topics, t] : [t],
              },
            };
          });

          setUpdatedTemplate((prevState) => {
            return {
              ...prevState,
              tags: [...prevState?.tags, t],
              metadata: {
                ...prevState.metadata,
                topics: prevState?.metadata?.topics ? [...prevState.metadata.topics, t] : [t],
              },
            };
          });
        }}
      />

      <Typography className="text-sm text-glooCharcoal font-semibold pb-2" variant="base">
        Banner Image
      </Typography>
      <CloudinaryImageUploader
        imageWidth={600}
        preview
        className="mb-4"
        existingImg={template?.imageUrl}
        handleImgSelect={(imgUrl) => {
          handleChange((prevState) => {
            return { ...prevState, imageUrl: imgUrl };
          });
          setUpdatedTemplate((prevState) => {
            return { ...prevState, imageUrl: imgUrl };
          });
        }}
      />

      <Typography className="text-sm text-glooCharcoal font-semibold pb-2" variant="base">
        Thumbnail Image
      </Typography>
      <CloudinaryImageUploader
        imageWidth={213}
        preview
        className="mb-4"
        existingImg={template?.thumbnailImageUrl}
        handleImgSelect={(imgUrl) => {
          handleChange((prevState) => {
            return { ...prevState, thumbnailImageUrl: imgUrl };
          });
          setUpdatedTemplate((prevState) => {
            return { ...prevState, thumbnailImageUrl: imgUrl };
          });
        }}
      />

      <Input
        value={template?.metadata?.author || ''}
        label="Author"
        className="mb-4"
        onChangeText={(t) => {
          handleChange((prevState) => {
            return { ...prevState, metadata: { ...prevState.metadata, author: t || undefined } };
          });
          setUpdatedTemplate((prevState) => {
            return { ...prevState, metadata: { ...prevState.metadata, author: t || undefined } };
          });
        }}
      />

      <Typography className="pb-2" variant="label">
        Long Description
        <span className="text-red text-xs">*</span>
      </Typography>
      <WysiwygEditor
        className="mb-8"
        minDivHeight="616px"
        height={600}
        value={template?.longDescription || ''}
        onChange={(value) => {
          handleChange((prevState) => {
            return { ...prevState, longDescription: value || undefined };
          });
          setUpdatedTemplate((prevState) => {
            return { ...prevState, longDescription: value || undefined };
          });
        }}
      />
      <Card className="p-0 mt-6 col-span-4 rows-span-2 shadow-none">
        <Typography className="text-thryveGray uppercase my-4" variant="h3">
          {template?.type} CONFIGURATION:
        </Typography>
        {template && renderTemplateConfigPartial(updatedTemplate)}
      </Card>
    </form>
  );
}

export default TemplateForm;
